import { createWithEqualityFn } from "zustand/traditional";
import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import devDebug from "../Helpers/debug";

const initialState = {
  loginDataVersion: null,
  otherDataVersion: null,
  // remoteFullUserReset: {}, // Keep if needed, seems unrelated to this issue
  timerStartTime: null, // Timestamp (ms) when the timer started
  initialTimerDuration: null, // Duration in seconds
  localTimerId: null, // For the setInterval ID, NOT persisted
};
const currentAppVersionFromEnv = Number(import.meta.env.VITE_VERSION);

export const useVersionChangeStore = createWithEqualityFn(
  persist(
    immer((set, get) => ({
      ...initialState,
      // ------------------- VERSION CHECKING --------------
      setTimerDetails: (startTime, duration) =>
        set((state) => {
          devDebug("Setting timer details: ", { startTime, duration });
          state.timerStartTime = startTime;
          state.initialTimerDuration = duration;
        }),
      clearTimerDetails: () =>
        set((state) => {
          devDebug("Clearing timer details");
          state.timerStartTime = null;
          state.initialTimerDuration = null;
        }),
      setLocalTimerId: (timerId) => // For managing the interval in the active tab
        set((state) => {
          devDebug("Setting localTimerId to: ", timerId);
          state.localTimerId = timerId;
        }),
      // timeLeft will be derived by components or the hook, not stored directly
      setLoginDataVersion: (loginDataVersion) =>
        set((state) => {
          devDebug("Setting loginDataVersion to: ", loginDataVersion);
          state.loginDataVersion = loginDataVersion;
        }),
      setOtherDataVersion: (otherDataVersion) =>
        set((state) => {
          devDebug("Setting otherDataVersion to: ", otherDataVersion);
          state.otherDataVersion = otherDataVersion;
        }),
      // setRemoteFullUserReset: (remoteFullUserReset) => // Keep if needed
      //   set((state) => {
      //     state.remoteFullUserReset = remoteFullUserReset;
      //   }),
    })),
    {
      name: "versionChangeStore",
      version: currentAppVersionFromEnv,
      partialize: (state) => ({
        // Only persist these parts
        loginDataVersion: state.loginDataVersion,
        otherDataVersion: state.otherDataVersion,
        timerStartTime: state.timerStartTime,
        initialTimerDuration: state.initialTimerDuration,
        // remoteFullUserReset: state.remoteFullUserReset, // if you need it persisted
      }),
      migrate: (persistedState, storedVersion) => {
        devDebug(
          `[versionChangeStore Migrate] does not have a migrate function intentionally`
        );
        if (storedVersion !== currentAppVersionFromEnv) {
          devDebug('[versionChangeStore Migrate] Versions differ but using persisted state.');
          return { ...persistedState };
        }
        if (persistedState === null || persistedState === undefined) {
          devDebug('[versionChangeStore Migrate] Persisted state is null/undefined. Resetting.');
          return { ...initialState, localTimerId: null };
        }
        devDebug('[versionChangeStore Migrate] Versions same. Using persisted state.');
        // Ensure localTimerId is reset on rehydration from persisted data
        return { ...persistedState, localTimerId: null };
      },
      onRehydrateStorage: () => (hydratedState, error) => {
        if (error) {
          console.error("[versionChangeStore] Failed to rehydrate store!", error);
          devDebug("[versionChangeStore] Error during rehydration. Store name: 'versionChangeStore'.", error);
        } else {
          // Ensure localTimerId is reset after rehydration as it's not meaningful across sessions/tabs
          hydratedState.setLocalTimerId(null)
          // devDebug("[versionChangeStore] Successfully rehydrated.", hydratedState);
        }
      },
    }
  ),
  // shallow // Using default equality function for createWithEqualityFn
);

// Helper to get current time left, can be used by hook or components
export const getCalculatedTimeLeft = (state) => {
  if (!state.timerStartTime || !state.initialTimerDuration) {
    return 0;
  }
  const elapsedSeconds = (Date.now() - state.timerStartTime) / 1000;
  return Math.max(0, Math.floor(state.initialTimerDuration - elapsedSeconds));
};